/**
 * The MuseTalk talking head (docs/NEW-UI/protocollo.md §7). Frames come over `/avatar-ws` (Vite proxy
 * to the avatar service :8020) with a header {turn, pts}; speaking frames are buffered by pts and, each
 * animation frame, the newest one at or before the played-audio clock is drawn — so the mouth matches
 * the sound. The first speaking frame of a reply opens the audio gate. Idle frames play in arrival
 * order at the end of a reply (seam), then latest-wins.
 *
 * Same algorithm as the old kiosk (backend/frontend/src/components/shared/MuseTalkAvatar.tsx); the
 * decisions are the tested pure functions of ./sync.ts and ./plate.ts.
 */
import { useEffect, useRef } from 'react';
import { report } from '../core/diagnostics';
import type { AvatarBridge } from '../voice/controller';
import {
  FRAME_HEADER_BYTES, LEAD_MS, MAX_BUFFER_MS, STALE_MS, SeamScheduler, avDebugEnabled, bufferDropThreshold,
  getAvsyncPersistent, parseFrameHeader, pickFrame, recordDraw, type FrameRef,
} from './sync';
import { recordFrameGap, recordPick } from './sync-telemetry';
import { PlateStore, decodeFrame, helloMatchesPlate, type BoccaHello } from './plate';

const WS_PATH = '/avatar-ws';
const RECONNECT_MS = 1500;
/** 5 s without frames on an open socket is a dead link (the idle stream runs at 25 fps). */
const NO_FRAME_TIMEOUT_MS = 5000;
const WATCHDOG_CHECK_MS = 1000;

interface BufferedFrame {
  bitmap: ImageBitmap;
  turn: number;
}

/** `onLive`: true at the first frame of a connection, false when it closes (the place shows its stand-in). */
export function MuseTalkAvatar({ bridge, plateId, onLive }: { bridge: AvatarBridge; plateId: string | null; onLive?: (live: boolean) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onLiveRef = useRef(onLive);
  useEffect(() => { onLiveRef.current = onLive; }, [onLive]);

  useEffect(() => {
    let closed = false;
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let rafId = 0;
    let lastFrameTs: number | null = null;
    let lastActiveMs = performance.now();
    const buffer = new Map<number, BufferedFrame>();
    const seam = new SeamScheduler<ArrayBuffer>();
    let idleBitmap: ImageBitmap | null = null;
    let idleGen = 0;
    let idleChain: Promise<void> = Promise.resolve();
    let shownPts: number | null = null;
    let firstFrameTurn: number | null = null;
    let plate: PlateStore | null = null;
    let boccaRefused = false;
    let live = false;
    const setLive = (now: boolean) => {
      if (now !== live) onLiveRef.current?.((live = now));
    };

    const draw = (bm: ImageBitmap) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      if (canvas.width !== bm.width || canvas.height !== bm.height) {
        canvas.width = bm.width;
        canvas.height = bm.height;
      }
      ctx.drawImage(bm, 0, 0);
    };

    const showIdle = (bytes: ArrayBuffer) => {
      const gen = idleGen;
      idleChain = idleChain.then(async () => {
        if (closed || gen !== idleGen) return;
        const bm = await decodeFrame(bytes, plate).catch(() => null);
        if (!bm) return;
        if (closed || gen !== idleGen) {
          bm.close();
          return;
        }
        draw(bm);
        idleBitmap?.close();
        idleBitmap = bm;
        shownPts = null;
      });
    };

    const prune = (turn: number) => {
      let newest = Number.NEGATIVE_INFINITY;
      for (const [pts, f] of buffer) {
        if (f.turn !== turn) {
          f.bitmap.close();
          buffer.delete(pts);
        } else if (pts > newest) newest = pts;
      }
      const floor = bufferDropThreshold(newest, MAX_BUFFER_MS);
      for (const [pts, f] of buffer) {
        if (pts < floor) {
          f.bitmap.close();
          buffer.delete(pts);
        }
      }
    };

    const clearBuffer = () => {
      for (const f of buffer.values()) f.bitmap.close();
      buffer.clear();
      firstFrameTurn = null;
      seam.reset();
      idleGen += 1;
      if (avDebugEnabled()) console.debug('[avdbg] avatar buffer cleared (barge-in)');
    };

    const onFrame = async (buf: ArrayBuffer) => {
      if (buf.byteLength === 0) return;
      setLive(true);
      const now = performance.now();
      if (lastFrameTs !== null) {
        const p = getAvsyncPersistent();
        if (p) recordFrameGap(p, now - lastFrameTs);
      }
      lastFrameTs = now;
      const header = parseFrameHeader(new DataView(buf));
      if (!header || header.idle) {
        // v2 keeps its header (it names the plate frame); v1 idle is the bare JPEG after the header.
        seam.enqueue(header && header.version >= 2 ? buf : header ? buf.slice(FRAME_HEADER_BYTES) : buf);
        return;
      }
      seam.onSpeakingFrame();
      const bm = await decodeFrame(buf, plate).catch(() => null);
      if (!bm) return;
      if (closed) {
        bm.close();
        return;
      }
      buffer.get(header.pts)?.bitmap.close();
      buffer.set(header.pts, { bitmap: bm, turn: header.turn });
      if (firstFrameTurn !== header.turn) {
        firstFrameTurn = header.turn;
        bridge.firstFrame(header.turn); // decoded first: the bitmap is ready when the sound starts
      }
      prune(header.turn);
    };

    const present = () => {
      if (closed) return;
      const clock = bridge.clock();
      const entries: FrameRef[] = [];
      for (const [pts, f] of buffer) entries.push({ pts, turn: f.turn });
      const now = performance.now();
      const result = pickFrame(entries, clock, { leadMs: LEAD_MS, staleMs: STALE_MS }, { nowMs: now, lastActiveMs });
      if (result.kind !== 'hold') lastActiveMs = now;
      const p = getAvsyncPersistent();
      if (p) recordPick(p, result.kind);
      if (result.kind === 'draw') {
        seam.onSpeakingDraw();
        idleGen += 1;
        if (clock) recordDraw(result.pts, clock.playedMs);
        if (result.pts !== shownPts) {
          const f = buffer.get(result.pts);
          if (f) {
            draw(f.bitmap);
            shownPts = result.pts;
          }
        }
        for (const [pts, f] of buffer) {
          if (pts < result.dropBelowPts) {
            f.bitmap.close();
            buffer.delete(pts);
          }
        }
      } else if (result.kind === 'idle') {
        const action = seam.onIdleTick(now);
        if (action.kind === 'present') showIdle(action.item);
      }
      rafId = requestAnimationFrame(present);
    };

    const onHello = (text: string) => {
      let hello: BoccaHello;
      try {
        hello = JSON.parse(text) as BoccaHello;
      } catch {
        return;
      }
      if (plate && hello.mode === 'bocca' && helloMatchesPlate(hello, plate.meta)) return;
      report('warn', 'avatar', 'solo bocca refused — full frames', { hello, plate: plate?.meta });
      if (hello.mode === 'bocca') {
        boccaRefused = true;
        ws?.close(); // reconnects without ?mode=bocca
      }
    };

    const connect = () => {
      if (closed) return;
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const mode = plate && !boccaRefused ? '?mode=bocca' : '';
      ws = new WebSocket(`${scheme}://${window.location.host}${WS_PATH}${mode}`);
      ws.binaryType = 'arraybuffer';
      ws.onopen = () => {
        bridge.setAvatarConnected(true);
        lastFrameTs = performance.now();
      };
      ws.onmessage = (ev) => {
        if (ev.data instanceof ArrayBuffer) void onFrame(ev.data);
        else if (typeof ev.data === 'string') onHello(ev.data);
      };
      ws.onclose = () => {
        bridge.setAvatarConnected(false);
        setLive(false);
        if (!closed) reconnectTimer = setTimeout(connect, RECONNECT_MS);
      };
      ws.onerror = () => ws?.close();
    };

    const watchdog = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN && lastFrameTs !== null && performance.now() - lastFrameTs > NO_FRAME_TIMEOUT_MS) {
        const p = getAvsyncPersistent();
        if (p) p.watchdogFires += 1;
        if (avDebugEnabled()) console.debug('[avdbg] no-frame watchdog → forcing WS reconnect');
        lastFrameTs = performance.now();
        ws.close();
      }
    }, WATCHDOG_CHECK_MS);

    bridge.onClear(clearBuffer);
    if (plateId) {
      PlateStore.load(plateId)
        .then((p) => { plate = p; })
        .catch((e) => report('warn', 'avatar', `plate ${plateId} not loaded — full frames`, e))
        .finally(connect);
    } else {
      connect();
    }
    rafId = requestAnimationFrame(present);

    return () => {
      closed = true;
      bridge.setAvatarConnected(false);
      bridge.onClear(null);
      clearTimeout(reconnectTimer);
      clearInterval(watchdog);
      cancelAnimationFrame(rafId);
      ws?.close();
      for (const f of buffer.values()) f.bitmap.close();
      buffer.clear();
      idleBitmap?.close();
    };
  }, [bridge, plateId]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} aria-hidden />;
}
